'use client';

import { useEffect } from 'react';

import { suggestEmailCorrection } from '@/lib/email-typo';
import { gtagEvent } from '@/utils/gtag';

type EmailTypoHintProps = {
  email: string;
  onAccept: (corrected: string) => void;
};

/**
 * Advisory "did you mean" under an email field. Renders nothing when the address looks
 * fine. Accepting is one click, and there is no way to block on it, because the domain
 * list cannot tell a small real domain from a typo of a large one.
 */
export function EmailTypoHint({ email, onAccept }: EmailTypoHintProps) {
  const suggestion = suggestEmailCorrection(email);

  // Keyed on the suggestion rather than the email, so holding one typo across several
  // keystrokes reports once instead of per character.
  useEffect(() => {
    suggestion && gtagEvent('email_typo_suggested');
  }, [suggestion]);

  if (!suggestion) return null;

  return (
    <p className='text-sm text-muted-foreground pt-1'>
      Did you mean{' '}
      <button
        className='font-medium text-foreground underline'
        onClick={() => {
          gtagEvent('email_typo_accepted');
          onAccept(suggestion);
        }}
        type='button'
      >
        {suggestion}
      </button>
      ?
    </p>
  );
}
